// Frontend fallback defaults. Mirrors backend content_service.DEFAULT_CONTENT so
// the public site renders correctly even if the API call fails.
export const DEFAULT_CONTENT = {
  global: {
    calendly_url: "https://calendly.com/see_chelle",
    linkedin_url: "https://www.linkedin.com/in/rochellecannington/",
    headshot_url: "",
    site_title: "See Chelle | Fundraising Consultant for Charities",
    site_description:
      "See Chelle provides practical fundraising support for small to medium charities, including appeals, donor engagement, fundraising reviews, copywriting and extra team capacity.",
  },
  hero: {
    eyebrow: "Fundraising Consultant · Melbourne, Australia",
    headline_pre: "Fundraising support that feels like",
    headline_em: "an extra member",
    headline_post: "of your team.",
    paragraph1:
      "You do not always need another staff member. Sometimes you just need someone who understands fundraising, can jump in quickly and get things done.",
    paragraph2:
      "With more than 20 years of fundraising experience, Rochelle works alongside charities to provide practical advice, strategic thinking and hands on support, so your team can raise more and worry less.",
    primary_cta: "Book a 20 minute chat",
    secondary_link: "Learn more about Rochelle",
  },
  social_proof: {
    label: "Trusted by organisations including",
    orgs: ["Dylan Alcott Foundation", "Bridge It", "Support Act", "St Kilda Mums"],
  },
  trust_strip: {
    items: [
      { headline: "20+ years", sub: "Fundraising experience" },
      { headline: "Small to medium", sub: "Charity specialist" },
      { headline: "Appeals · Donors · Strategy", sub: "Practical, hands on support" },
      { headline: "Melbourne · Australia wide", sub: "Working with teams remotely" },
    ],
  },
  about: {
    eyebrow: "About Rochelle",
    heading_pre: "Hi, I'm",
    heading_em: "Rochelle.",
    paragraphs: [
      "I started working in fundraising at 17, doing telemarketing, and have spent more than two decades building a career across fundraising and communications for some wonderful charities.",
      "I founded See Chelle in 2023 to support more organisations and share my experience where it can make the biggest difference. I work alongside small to medium charities, getting strategic, getting practical, and getting things done.",
      "I'm based in Melbourne and partner with teams right across Australia.",
    ],
    linkedin_label: "Connect with Rochelle on LinkedIn",
  },
  why: {
    eyebrow: "Why work with me",
    heading_pre: "Because",
    heading_em: "I get it.",
    body:
      "Fundraising teams are often juggling appeals, grants, donor communications, reporting, events and strategy, all at once. When there aren't enough hours in the day, I can step in with calm, practical support and help move the work forward.",
  },
  services: {
    eyebrow: "Services",
    heading: "Practical fundraising support, shaped around your organisation.",
    cards: [
      { icon: "clipboard", title: "Fundraising Reviews", body: "A clear review of your fundraising program with practical recommendations, growth opportunities and achievable easy wins." },
      { icon: "megaphone", title: "Appeal Management", body: "Help planning and running fundraising appeals that connect with donors and raise what you need." },
      { icon: "heart", title: "Donor Engagement", body: "Thoughtful stewardship and communication strategies to help build stronger, longer term supporter relationships." },
      { icon: "pen", title: "Copywriting & Editing", body: "Appeals, donor communications, impact reports, newsletters, website copy and more." },
      { icon: "users", title: "Extra Capacity", body: "Hands on support when your team needs extra help during busy periods, campaign peaks, maternity leave or staff gaps." },
    ],
    cta_lede: "Have something else in mind? Book a chat and let's talk it through.",
    cta_label: "Book a 20 minute chat",
  },
  how: {
    eyebrow: "How we can work together",
    heading: "Flexible support that fits your team.",
    supporting: "Every organisation is different, so I keep things flexible. I focus on making every hour count.",
    options: [
      { n: "01", title: "Hourly support", body: "Targeted help for specific projects or moments." },
      { n: "02", title: "Short term contracts", body: "Defined scope, clear deliverables, no fuss." },
      { n: "03", title: "Longer term partnerships", body: "Embedded support over months or seasons." },
      { n: "04", title: "Extra team capacity", body: "Step in support when your team is stretched." },
    ],
  },
  testimonials: {
    eyebrow: "Kind words",
    heading: "Kind words from people I have worked with.",
    items: [
      { quote: "Rochelle has been an invaluable part of our team in running our first ever Giving Day. Her knowledge and skills in the fundraising space are unmatched and it is her enthusiasm and creativity that makes her an asset to our foundation and team.", name: "Georgie Saggers", role: "General Manager", org: "Dylan Alcott Foundation" },
      { quote: "I connected with Rochelle on LinkedIn after seeing one of my funders refer to her as “the best in the biz.” I can agree wholeheartedly that that statement is totally true. Rochelle helped me to get very strategic with my fundraising, see where I should focus my limited time and secure funds with less of the stress.", name: "Carla Raynes", role: "CEO and Founder", org: "Bridge It" },
      { quote: "Rochelle provided maternity leave cover for our Individual Giving Manager from August to December 2025 and was an absolute pleasure to work with. She quickly understood each brief and consistently delivered high quality copy and well considered campaign plans.", name: "Clive Miller", role: "CEO", org: "Support Act" },
    ],
    orgs_line: "Rochelle has worked with organisations including Dylan Alcott Foundation · Bridge It · St Kilda Mums · The Nappy Collective · Support Act · The Babes Project.",
  },
  mission_vision: {
    mission_eyebrow: "Mission",
    mission_pre: "Helping small to medium charities with small to medium projects that make a",
    mission_em: "large difference.",
    vision_eyebrow: "Vision",
    vision_pre: "A world where every charity has the tools and strategies to create a",
    vision_em: "lasting impact",
    vision_post: "for their cause and the people they help.",
  },
  contact: {
    eyebrow: "Get in touch",
    heading_pre: "Let's see how",
    heading_em: "I can help.",
    lede: "The best way to find out if we are a good fit is to have a conversation. Book a 20 minute chat, tell me about your organisation, and we can talk through what support would be most useful.",
    secondary_note: "Or send a quick note using the form. I'll come back to you soon.",
    calendly_note: "Calendly is still the quickest way to chat.",
    success_heading: "Thanks for getting in touch.",
    success_body: "Rochelle will come back to you soon. In the meantime, feel free to book a 20 minute chat below.",
  },
  footer: {
    tagline: "Fundraising support for charities that need practical, experienced help.",
    based_in_label: "Based in",
    based_in: "Melbourne, Australia\nWorking with charities across Australia.",
    acknowledgement:
      "See Chelle acknowledges the Traditional Custodians of the lands on which we live and work, and pays respect to Elders past and present. Sovereignty was never ceded.",
    signature: "Made with care.",
  },
};
